import { Bell, Mic } from 'lucide-preact';
import { useMemo, useState } from 'preact/hooks';

import HabitatLink from '@/components/HabitatLink';
import TalkReminderModal from '@/components/TalkReminderModal';
import { formatDateRange } from '@/lib/date';
import { Event } from '@/queries/events/types';
import { useSelector } from '@/redux/helper';
import { trackMP } from '@/shared/mp';

interface UpcomingEventItemProps
  extends Pick<
    Event,
    | '_id'
    | 'animal'
    | 'businessHourId'
    | 'habitatId'
    | 'habitatImage'
    | 'habitatSlug'
    | 'zooSlug'
    | 'zooLogo'
    | 'title'
    | 'description'
    | 'startTime'
    | 'stopTime'
    | 'host'
  > {}

const UpcomingEventItem = ({
  _id,
  animal,
  businessHourId,
  habitatId,
  habitatImage,
  habitatSlug,
  zooSlug,
  zooLogo,
  title,
  description,
  startTime,
  stopTime,
  host,
}: UpcomingEventItemProps) => {
  const [showReminderModal, setShowReminderModal] = useState(false);
  const isLogged = useSelector((state) => state.user.logged);
  const { isOver, isHappening, label } = useMemo(() => {
    const now = Date.now();
    const start = Date.parse(startTime);
    const stop = Date.parse(stopTime);
    const isHappening = now >= start && now < stop;
    return {
      isOver: now >= stop,
      isHappening,
      label: isHappening ? undefined : formatDateRange(start, stop),
    };
  }, [startTime, stopTime]);

  if (isOver) {
    return null;
  }

  return (
    <div className="py-3">
      <div className="grid grid-cols-[60px_1fr] gap-4">
        <div>
          <div className="relative">
            <img
              src={habitatImage}
              alt={title}
              className="preflight preflight-img aspect-square size-[60px] rounded-md object-cover"
            />
            {zooLogo && (
              <div className="absolute -bottom-2 -right-2 z-10 flex size-[30px] items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_4px_4px_rgba(0,0,0,0.15)]">
                <img className="preflight preflight-img size-full object-contain" src={zooLogo} alt={zooSlug} />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-[3px]">
          <h4 className="preflight preflight-h4 text-sm/4 font-bold">{title}</h4>
          {host?.username && (
            <div className="flex items-center gap-0.5">
              <Mic className="size-[11px] text-stone-500" />
              <p className="preflight preflight-p text-[10px] font-bold uppercase leading-4 text-stone-500">
                Hosted by {host.username}
              </p>
            </div>
          )}
          <p className="preflight preflight-p line-clamp-2 text-xs leading-normal text-stone-500">{description}</p>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <HabitatLink
          productsPromptModalState={{ entryPoint: 'upcoming-events-card' }}
          habitatId={habitatId}
          slug={habitatSlug}
          zooSlug={zooSlug}
          className="preflight preflight-a flex h-[23px] items-center justify-center rounded-full bg-primary-green px-4 text-xs font-medium text-white hover:bg-secondary-green"
          onClick={() => {
            trackMP('visit-habitat-link-clicked', {
              habitatId,
              entryPoint: 'upcoming-events-card',
            });
          }}
        >
          {isHappening ? 'Join' : 'Visit'}
        </HabitatLink>
        <div className="flex items-center gap-2">
          {label && <p className="preflight preflight-p text-[11px] font-bold text-primary-green">{label}</p>}
          {!isHappening && isLogged && (
            <button
              type="button"
              className="preflight preflight-button flex size-[22px] items-center justify-center rounded-full bg-primary-green text-white hover:bg-secondary-green"
              onClick={() => setShowReminderModal(true)}
            >
              <Bell className="size-3.5" />
              <span className="sr-only">Remind me</span>
            </button>
          )}
        </div>
      </div>
      {showReminderModal && (
        <TalkReminderModal
          id={_id}
          animal={animal}
          businessHourId={businessHourId}
          startTime={startTime}
          stopTime={stopTime}
          eventTitle={title}
          eventDescription={description}
          url={`${window.location.origin}/h/${zooSlug}/${habitatSlug}`}
          onClose={() => setShowReminderModal(false)}
        />
      )}
    </div>
  );
};

export default UpcomingEventItem;
